import type * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjs from 'rxjs'

import type { Plugin } from '../plugin'

const SOURCE = 'applyPublishedMessages (effect)'

/**
 * Keeps the conversation's messages up to date from what the backend
 * publishes.
 *
 * Takes the place of the collection's generic change subscription, which
 * assumes every update is a whole entity.
 */
export const applyPublishedMessages = store.effects.create(
  (feature: Plugin['feature'], plugin: Plugin) => {
    /**
     * Stores a message and admits it to the conversation view.
     *
     * The cache holds entity contents; the current list decides which of them
     * the view shows, and in what order. A message reaching the view needs
     * both.
     */
    const cacheMessage = (
      message: ents.core.agent.Message,
    ): rxjs.Observable<store.actions.Action.Constraint> => {
      // Retrying a message restarts it as pending under the same id. Clear the
      // entries from the failed attempt so its text doesn't show in the new
      // reply. On a first attempt there is nothing to clear.
      const restarting =
        message.role === 'agent' && message.status === 'pending'

      return rxjs.from([
        ...(restarting
          ? [
              feature.actions.dropInfoEntries
                .from(SOURCE)
                .create({ messageId: message.id }),
            ]
          : []),
        // Otherwise a publication carries no entries, so those the message was
        // read with are kept.
        plugin.collection.plugins.cachePlugin.feature.actions.patch
          .from(SOURCE)
          .create({
            id: message.id,
            update: cached => ({
              ...message,
              info: restarting ? [] : (cached?.info ?? []),
            }),
          }),
        // Adding an id already listed is a no-op, so this covers a message's
        // first publication without needing to detect which one that was.
        plugin.collection.plugins.currentListPlugin.feature.actions.insert
          .from(SOURCE)
          .create({ ids: [message.id] }),
      ])
    }

    // Updates are scoped to one conversation, so the subscription follows the
    // collection's context: switchMap drops the previous conversation's when
    // the user moves to another.
    return plugin.collection.feature.selectors.context.asObservable.pipe(
      rxjs.switchMap(context =>
        plugin.api.onMessage(context).pipe(rxjs.concatMap(cacheMessage)),
      ),
    )
  },
)
